/*
 * ITG: automatic region detection on first visit.
 *
 * Design goals (hard-earned from a real incident where Canary customers
 * were briefly shown mainland ×1.21 prices):
 *
 *   1. Bias toward 'canarias'. Misclassifying a canary customer as
 *      mainland is expensive (overpriced, angry customer, refund).
 *      Misclassifying a mainland customer as canary is cheap — the
 *      checkout shipping zone will still charge them correctly and
 *      they get a small break on the product markup. We accept that.
 *   2. Never switch to 'mainland' on a single ambiguous signal. Require
 *      TWO independent agreeing signals (timezone + geo, or two geo
 *      providers agreeing).
 *   3. Multi-provider geo. If ipapi.co is rate-limited, blocked, or
 *      wrong, fall back to ipwho.is and geojs.io. Consensus wins.
 *   4. Persist successful detections with a 30-day TTL so returning
 *      visitors are flash-free and don't re-hit the geo APIs.
 *   5. Never overwrite a manual customer choice (cart.attributes
 *      .itg_region is treated as authoritative).
 *   6. Record which signal(s) fired into itg_region_source so we can
 *      diagnose future issues from the order record.
 *
 * Spain region_code note: providers return the PROVINCE code for Spanish
 * IPs. Canary provinces are TF (Santa Cruz de Tenerife) and GC (Las
 * Palmas). CN is the autonomous-community code. IC is an older informal
 * code. Any of them means Canary Islands.
 */
(function () {
  const STORAGE_KEY_REGION = 'itg_region';
  const STORAGE_KEY_TIMESTAMP = 'itg_region_detected_at';
  const STORAGE_KEY_MANUAL = 'itg_region_manual';
  const CACHE_TTL_MS = 30 * 24 * 60 * 60 * 1000; // 30 days
  const JUST_SWITCHED_KEY = 'itg_region_just_switched';
  const AUTO_ATTEMPTED_KEY = 'itg_region_auto_attempted';

  const GEO_TIMEOUT_MS = 3500;

  // Geo providers, tried in parallel. Each maps its response to a
  // normalized { country, region_code, region_name, timezone } shape.
  const GEO_PROVIDERS = [
    {
      name: 'ipapi',
      url: 'https://ipapi.co/json/',
      normalize: function (j) {
        return {
          country: j && j.country_code ? String(j.country_code).toUpperCase() : null,
          region_code: j && j.region_code ? String(j.region_code).toUpperCase() : null,
          region_name: j && j.region ? String(j.region) : null,
          timezone: j && j.timezone ? String(j.timezone) : null
        };
      }
    },
    {
      name: 'ipwho',
      url: 'https://ipwho.is/',
      normalize: function (j) {
        return {
          country: j && j.country_code ? String(j.country_code).toUpperCase() : null,
          region_code: j && j.region_code ? String(j.region_code).toUpperCase() : null,
          region_name: j && j.region ? String(j.region) : null,
          timezone: j && j.timezone && j.timezone.id ? String(j.timezone.id) : null
        };
      }
    },
    {
      name: 'geojs',
      url: 'https://get.geojs.io/v1/ip/geo.json',
      normalize: function (j) {
        return {
          country: j && j.country_code ? String(j.country_code).toUpperCase() : null,
          region_code: j && j.region ? String(j.region).toUpperCase() : null,
          region_name: j && j.region ? String(j.region) : null,
          timezone: j && j.timezone ? String(j.timezone) : null
        };
      }
    }
  ];

  // Any of these values, in a Spain response, means Canary Islands.
  const CANARY_REGION_CODES = ['CN', 'TF', 'GC', 'IC'];
  const CANARY_REGION_NAMES = /canar|tenerife|las palmas|gran canaria|lanzarote|fuerteventura|hierro|gomera|palma/i;

  function getRootRoute() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  }

  function getBrowserTimezone() {
    try {
      if (window.Intl && Intl.DateTimeFormat) {
        return Intl.DateTimeFormat().resolvedOptions().timeZone || null;
      }
    } catch (e) {}
    return null;
  }

  function regionFromTimezone(tz) {
    if (!tz) return null;
    if (tz === 'Atlantic/Canary') return 'canarias';
    if (tz === 'Europe/Madrid' || tz === 'Europe/Ceuta') return 'mainland';
    return null;
  }

  // Classify a normalized geo response. Returns 'canarias', 'mainland',
  // or null if the provider didn't say anything useful.
  function classifyGeo(g) {
    if (!g) return null;
    // Strong Canary signal from timezone in the response itself.
    if (g.timezone === 'Atlantic/Canary') return 'canarias';
    if (!g.country) return null;
    if (g.country !== 'ES') {
      // Outside Spain — we lean mainland by convention (they'll pay the
      // markup on the twin catalog), but this is only a single signal
      // and won't win alone against timezone: Atlantic/Canary.
      return 'mainland';
    }
    // Spain: check region_code and region_name for Canary markers.
    if (g.region_code && CANARY_REGION_CODES.indexOf(g.region_code) !== -1) return 'canarias';
    if (g.region_name && CANARY_REGION_NAMES.test(g.region_name)) return 'canarias';
    // Spain, no Canary markers → mainland.
    return 'mainland';
  }

  function fetchJson(url) {
    const controller = new AbortController();
    const timer = setTimeout(function () { controller.abort(); }, GEO_TIMEOUT_MS);
    return fetch(url, { signal: controller.signal, credentials: 'omit' })
      .then(function (r) {
        clearTimeout(timer);
        if (!r.ok) throw new Error('bad status ' + r.status);
        return r.json();
      })
      .catch(function (err) {
        clearTimeout(timer);
        throw err;
      });
  }

  // Fires all providers in parallel with a global timeout. Each provider
  // that succeeds contributes its classification (canarias/mainland/null).
  // We resolve as soon as ALL have settled (success or fail) — Promise
  // .allSettled — so we can vote across the survivors.
  function collectGeoVotes() {
    return Promise.allSettled(
      GEO_PROVIDERS.map(function (p) {
        return fetchJson(p.url).then(function (j) {
          var norm = p.normalize(j);
          return { provider: p.name, classification: classifyGeo(norm), raw: norm };
        });
      })
    ).then(function (settled) {
      var votes = [];
      settled.forEach(function (s) {
        if (s.status === 'fulfilled' && s.value && s.value.classification) {
          votes.push(s.value);
        }
      });
      return votes;
    });
  }

  // Decision engine. Returns { region, source } or null.
  //
  // Rule set (in order):
  //   A. If browser timezone === Atlantic/Canary → canarias (unbreakable,
  //      unique to the islands, not affected by VPN).
  //   B. Collect geo votes from all providers in parallel.
  //      B1. Any vote for canarias → canarias. Under-charging a mainland
  //          visitor is preferable to over-charging a canary visitor,
  //          and only Canary IPs report Canary markers, so a single
  //          canarias vote is very high signal.
  //      B2. To go to mainland, we require CONSENSUS: at least two
  //          independent votes, both mainland, AND timezone (if known)
  //          must be either mainland or unknown. A single mainland vote
  //          combined with an Atlantic/Canary timezone → treat as
  //          canarias (VPN or wrong IP db).
  //   C. If geo failed entirely and timezone is mainland → mainland.
  //   D. Otherwise: null (leave the theme default of 'canarias' alone).
  async function decideRegion() {
    var tz = getBrowserTimezone();
    var tzRegion = regionFromTimezone(tz);

    // A. Timezone Atlantic/Canary is decisive.
    if (tzRegion === 'canarias') {
      return { region: 'canarias', source: 'tz:' + tz };
    }

    // B. Multi-provider geo vote.
    var votes = [];
    try {
      votes = await collectGeoVotes();
    } catch (e) { /* fall through */ }

    var canaryVotes = votes.filter(function (v) { return v.classification === 'canarias'; });
    var mainlandVotes = votes.filter(function (v) { return v.classification === 'mainland'; });

    // B1. Any provider saw Canary markers — trust it.
    if (canaryVotes.length >= 1) {
      var names = canaryVotes.map(function (v) { return v.provider; }).join('+');
      return { region: 'canarias', source: 'geo-canary:' + names };
    }

    // B2. Mainland requires CONSENSUS.
    if (mainlandVotes.length >= 2) {
      // Guard: if the browser timezone contradicts by being Atlantic/Canary,
      // the votes are already invalidated in step A. This branch means
      // tz is either mainland or unknown. Safe to go mainland.
      var mNames = mainlandVotes.map(function (v) { return v.provider; }).join('+');
      return { region: 'mainland', source: 'geo-consensus:' + mNames };
    }

    // Only one mainland vote and no timezone corroboration → not enough.
    if (mainlandVotes.length === 1 && tzRegion === 'mainland') {
      return { region: 'mainland', source: 'geo+tz:' + mainlandVotes[0].provider + '+' + tz };
    }

    // C. Geo entirely inconclusive, but timezone said mainland.
    if (mainlandVotes.length === 0 && tzRegion === 'mainland') {
      return { region: 'mainland', source: 'tz-only:' + tz };
    }

    // D. Nothing decisive — fall through to theme default ('canarias').
    return null;
  }

  async function persistRegion(region, source) {
    try {
      await fetch(`${getRootRoute()}cart/update.js`, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify({
          attributes: {
            itg_region: region,
            itg_region_auto_detected: 'true',
            itg_region_manual: '',
            itg_region_source: source
          }
        })
      });
    } catch (e) { return; }
    try {
      localStorage.setItem(STORAGE_KEY_REGION, region);
      localStorage.setItem(STORAGE_KEY_TIMESTAMP, String(Date.now()));
      // Auto-detect never sets the manual flag — clear it if present.
      localStorage.removeItem(STORAGE_KEY_MANUAL);
    } catch (e) {}
    try { sessionStorage.setItem(JUST_SWITCHED_KEY, region); } catch (e) {}
    window.location.reload();
  }

  function hasFreshCachedRegion() {
    try {
      var cached = localStorage.getItem(STORAGE_KEY_REGION);
      if (cached !== 'canarias' && cached !== 'mainland') return false;
      var ts = parseInt(localStorage.getItem(STORAGE_KEY_TIMESTAMP) || '0', 10);
      // If the cache has no timestamp (legacy or pre-hardening state),
      // trigger a fresh detection ONCE and let it stamp a new timestamp.
      // Trusting it forever means devices that lived through the buggy
      // period can never self-heal.
      if (!ts) return false;
      return (Date.now() - ts) < CACHE_TTL_MS;
    } catch (e) { return false; }
  }

  function hasManualChoice() {
    try {
      return localStorage.getItem(STORAGE_KEY_MANUAL) === 'true';
    } catch (e) { return false; }
  }

  async function run() {
    // Skip if we already tried this session.
    let alreadyAttempted = false;
    try { alreadyAttempted = sessionStorage.getItem(AUTO_ATTEMPTED_KEY) === 'true'; } catch (e) {}
    if (alreadyAttempted) return;

    // MANUAL CHOICE WINS FOREVER on this device. If the customer ever
    // picked a region manually, we never override — not on cache expiry,
    // not on cart cookie reset, not on VPN. The only way to reset is
    // clearing site data.
    if (hasManualChoice()) {
      try { sessionStorage.setItem(AUTO_ATTEMPTED_KEY, 'true'); } catch (e) {}
      // Reconcile cart if localStorage says X but cart says empty/other.
      // Fires and forgets — we don't want to block the page on it.
      reconcileCartFromLocalStorage();
      return;
    }

    // Skip if we already have a fresh cached detection — the synchronous
    // <head> script already applied it to <html data-itg-region>.
    if (hasFreshCachedRegion()) {
      try { sessionStorage.setItem(AUTO_ATTEMPTED_KEY, 'true'); } catch (e) {}
      reconcileCartFromLocalStorage();
      return;
    }

    // Skip if the customer already has a region attribute on their cart
    // (either from a previous session's persisted detection, or from a
    // manual choice via the region selector).
    let cart;
    try {
      const r = await fetch(`${getRootRoute()}cart.js`, { credentials: 'same-origin' });
      if (!r.ok) return;
      cart = await r.json();
    } catch (e) { return; }
    if (cart && cart.attributes && cart.attributes.itg_region) {
      // Sync localStorage so the synchronous <head> script picks it up
      // next visit even if the cart cookie expires.
      try {
        localStorage.setItem(STORAGE_KEY_REGION, cart.attributes.itg_region);
        localStorage.setItem(STORAGE_KEY_TIMESTAMP, String(Date.now()));
        if (cart.attributes.itg_region_manual === 'true') {
          localStorage.setItem(STORAGE_KEY_MANUAL, 'true');
        }
      } catch (e) {}
      try { sessionStorage.setItem(AUTO_ATTEMPTED_KEY, 'true'); } catch (e) {}
      return;
    }

    const decision = await decideRegion();
    // Only mark the session as attempted AFTER we actually decided
    // something — otherwise a transient network failure would leave us
    // stuck for the whole session (finding #9 in the audit).
    if (!decision) return;
    try { sessionStorage.setItem(AUTO_ATTEMPTED_KEY, 'true'); } catch (e) {}

    await persistRegion(decision.region, decision.source);
  }

  /* Reconcile: if localStorage has a region but the cart doesn't (Shopify
   * reset the cart cookie, or the visitor arrived via a link that didn't
   * carry the cookie), push localStorage back into cart.attributes so
   * every Liquid partial and JS consumer stays in agreement. Also flips
   * the reverse: if cart has region but localStorage is empty, sync it.
   *
   * Fires and forgets — never blocks the page. */
  async function reconcileCartFromLocalStorage() {
    let cached = null;
    let manual = false;
    try {
      cached = localStorage.getItem(STORAGE_KEY_REGION);
      manual = localStorage.getItem(STORAGE_KEY_MANUAL) === 'true';
    } catch (e) {}
    if (cached !== 'canarias' && cached !== 'mainland') return;

    let cart;
    try {
      const r = await fetch(`${getRootRoute()}cart.js`, { credentials: 'same-origin' });
      if (!r.ok) return;
      cart = await r.json();
    } catch (e) { return; }

    var cartRegion = cart && cart.attributes && cart.attributes.itg_region;
    if (cartRegion === cached) return; // Already agree.

    try {
      await fetch(`${getRootRoute()}cart/update.js`, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify({
          attributes: {
            itg_region: cached,
            itg_region_source: manual ? 'manual' : 'reconciled',
            itg_region_manual: manual ? 'true' : ''
          }
        })
      });
    } catch (e) {}
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', run);
  } else {
    run();
  }
})();
